/**
 * Cart Add-On Manager
 *
 * Handles add-on upsell interactions within the cart page:
 * - Dropdown triggers for drawer-mode addon groups
 * - Checkbox selection for simple-mode addon groups (single and multi-select)
 * - Adding and removing addon items from the cart
 * - Auto-removing orphaned addon items when their parent is removed
 * - Global loading overlay during async cart operations
 * - Refreshing cart UI after changes
 */
class CartAddonManager {
  /** @type {boolean} */
  initialized = false;

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.setupDrawerTriggers();
    this.setupSimpleCheckboxes();
    this.setupSimpleRemoveButtons();
    this.setupGroupMoreInfoPopups();
    this.setupAddonCleanup();
  }

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /** @returns {string} */
  generateUniqueId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /** @param {string} [message] */
  showGlobalLoading(message = 'Processing...') {
    this.hideGlobalLoading();

    const overlay = document.createElement('div');
    overlay.id = 'cart-addon-global-loading';
    overlay.className = 'cart-addon-loading-overlay';
    overlay.innerHTML = `
      <div class="cart-addon-loading-content">
        <div class="cart-addon-spinner"></div>
        <p class="cart-addon-loading-text">${message}</p>
      </div>
    `;

    // Append inside the cart drawer dialog if open, so the overlay
    // renders in the browser's top layer above the dialog
    const drawerDialog = document.querySelector('cart-drawer-component dialog[open]');
    if (drawerDialog) {
      drawerDialog.appendChild(overlay);
    } else {
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';
    }

    requestAnimationFrame(() => {
      overlay.classList.add('active');
    });
  }

  hideGlobalLoading() {
    const overlay = document.getElementById('cart-addon-global-loading');
    if (overlay) {
      overlay.classList.remove('active');
      setTimeout(() => {
        // Only restore body overflow if overlay was on body
        if (overlay.parentNode === document.body) {
          document.body.style.overflow = '';
        }
        overlay.remove();
      }, 300);
    }
  }

  // ============================================================================
  // DROPDOWN FUNCTIONALITY
  // ============================================================================

  /** @param {HTMLElement} trigger */
  toggleCartAddonDropdown(trigger) {
    const dropdown = trigger.nextElementSibling;
    const addonCategory = trigger.closest('.cart-addon-category-new');

    if (!dropdown || !addonCategory) return;

    const isActive = dropdown.classList.contains('active');

    // Close all other dropdowns
    document.querySelectorAll('.cart-addon-dropdown-content-new.active').forEach((content) => {
      content.classList.remove('active');
      const triggerBtn = content.previousElementSibling;
      if (triggerBtn && triggerBtn.classList.contains('cart-addon-trigger-new')) {
        triggerBtn.classList.remove('active');
      }
    });

    if (!isActive) {
      dropdown.classList.add('active');
      trigger.classList.add('active');

      // Close dropdown when clicking outside
      setTimeout(() => {
        /** @param {MouseEvent} e */
        const closeDropdown = (e) => {
          const target = /** @type {Node} */ (e.target);
          if (!addonCategory.contains(target)) {
            dropdown.classList.remove('active');
            trigger.classList.remove('active');
            document.removeEventListener('click', closeDropdown);
          }
        };
        document.addEventListener('click', closeDropdown);
      }, 0);
    } else {
      dropdown.classList.remove('active');
      trigger.classList.remove('active');
    }
  }

  // ============================================================================
  // ADD ADDON TO CART
  // ============================================================================

  /**
   * @param {string} variantId
   * @param {string} mainVariantId
   * @param {number} quantity
   * @param {string} groupId
   * @param {string} addonGroupIndex
   * @param {string | null} categoryIndex
   * @param {string | null} categoryTitle
   */
  async addAddonToCart(
    variantId,
    mainVariantId,
    quantity,
    groupId,
    addonGroupIndex,
    categoryIndex,
    categoryTitle
  ) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));
    formData.append('properties[_product_group_id]', groupId);
    formData.append('properties[_is_upsell]', 'true');
    formData.append('properties[_upsell_for]', mainVariantId);
    formData.append('properties[_upsell_to]', '');
    formData.append('properties[_addon_group_index]', addonGroupIndex);

    if (categoryIndex !== undefined && categoryIndex !== null) {
      formData.append('properties[_addon_category_index]', categoryIndex);
    }
    if (categoryTitle) {
      formData.append('properties[_addon_category_title]', categoryTitle);
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (data.status && data.status === 422) {
      throw new Error(data.description || 'Unable to add item to cart');
    }

    return data;
  }

  /**
   * Ensures the main product has a group ID. If it already has one, returns it.
   * If it doesn't, sets the provided groupId on the main item and returns it.
   * @param {string} mainVariantId
   * @param {string} groupId
   * @returns {Promise<string>} The group ID that was set or already existed
   */
  async updateMainProductGroupId(mainVariantId, groupId) {
    const cartResponse = await fetch('/cart.json');
    const cart = await cartResponse.json();
    const items = /** @type {Array<Record<string, unknown>>} */ (cart.items);

    // First check if main item already has a group ID — if so, reuse it
    const existingItem = items.find(
      (item) =>
        item['variant_id'] == mainVariantId &&
        item['properties'] &&
        /** @type {Record<string, string>} */ (item['properties'])['_product_group_id']
    );
    if (existingItem) {
      return /** @type {string} */ (
        /** @type {Record<string, string>} */ (existingItem['properties'])['_product_group_id']
      );
    }

    // No existing group ID — find the item without one and set it
    const mainItemIndex = items.findIndex(
      (item) =>
        item['variant_id'] == mainVariantId &&
        (!item['properties'] ||
          !(/** @type {Record<string, string>} */ (item['properties'])['_product_group_id']))
    );

    if (mainItemIndex === -1) return groupId;

    const mainItem = items[mainItemIndex];
    const updatedProperties = {
      .../** @type {object} */ (mainItem['properties']),
      _product_group_id: groupId,
      _is_upsell: 'false',
    };

    const updateResponse = await fetch('/cart/change.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: mainItemIndex + 1,
        quantity: mainItem['quantity'],
        properties: updatedProperties,
      }),
    });

    if (!updateResponse.ok) {
      throw new Error('Failed to update main product properties');
    }

    await updateResponse.json();
    return groupId;
  }

  // ============================================================================
  // REMOVE ADDON FROM CART
  // ============================================================================

  /** @param {number} line */
  removeAddonLine(line) {
    return fetch('/cart/change.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity: 0 }),
    });
  }

  // ============================================================================
  // SIMPLE MODE
  // ============================================================================

  /** @param {HTMLInputElement} input */
  async handleSimpleAddonChange(input) {
    const group = /** @type {HTMLElement | null} */ (input.closest('.cart-simple-addon-group-new'));
    if (!group) return;

    const isMultiSelect = input.dataset.isMultiSelect === 'true';
    const variantId = input.dataset.variantId ?? '';
    const mainVariantId = group.dataset.mainVariantId ?? '';
    const mainGroupId = group.dataset.mainGroupId ?? '';
    const groupIndex = group.dataset.groupIndex ?? '';

    if (!isMultiSelect) {
      if (input.checked) {
        this.showGlobalLoading('Updating selection...');

        try {
          const cartResponse = await fetch('/cart.json');
          const cart = await cartResponse.json();

          // Find and remove any existing addon for this group
          let existingAddonLine = -1;
          const items = /** @type {Array<Record<string, unknown>>} */ (cart.items);
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item) continue;
            const props = /** @type {Record<string, string>} */ (item['properties'] ?? {});
            if (
              props['_is_upsell'] === 'true' &&
              props['_upsell_for'] == mainVariantId &&
              props['_addon_group_index'] == groupIndex &&
              (mainGroupId === '' || props['_product_group_id'] === mainGroupId)
            ) {
              existingAddonLine = i + 1;
              break;
            }
          }

          if (existingAddonLine !== -1) {
            await this.removeAddonLine(existingAddonLine);
            await new Promise((resolve) => setTimeout(resolve, 300));
          }

          // Uncheck all other checkboxes in this group
          group.querySelectorAll('.cart-simple-addon-checkbox-new').forEach((el) => {
            if (el !== input) /** @type {HTMLInputElement} */ (el).checked = false;
          });

          await this.handleSimpleAddonAdd(variantId, mainVariantId, mainGroupId, groupIndex, input);
        } catch (error) {
          console.error('Error updating selection:', error);
          input.checked = false;
          this.hideGlobalLoading();
          alert('Unable to update selection');
        }
      } else {
        this.showGlobalLoading('Removing item...');
        this.handleSimpleAddonRemove(variantId, mainGroupId, input, false);
      }
    } else {
      this.showGlobalLoading(input.checked ? 'Adding item...' : 'Removing item...');

      if (input.checked) {
        this.handleSimpleAddonAdd(variantId, mainVariantId, mainGroupId, groupIndex, input);
      } else {
        this.handleSimpleAddonRemove(variantId, mainGroupId, input, false);
      }
    }
  }

  /**
   * @param {string} variantId
   * @param {string} mainVariantId
   * @param {string} mainGroupId
   * @param {string} groupIndex
   * @param {HTMLInputElement} input
   */
  async handleSimpleAddonAdd(variantId, mainVariantId, mainGroupId, groupIndex, input) {
    try {
      const cartResponse = await fetch('/cart.json');
      const cart = await cartResponse.json();
      const items = /** @type {Array<Record<string, unknown>>} */ (cart.items);
      const mainItem = items.find((item) => item['variant_id'] == mainVariantId);
      const cartQuantity = mainItem ? /** @type {number} */ (mainItem['quantity']) : 1;

      let groupId = mainGroupId;
      if (!groupId || groupId === '') {
        groupId = this.generateUniqueId();
        groupId = await this.updateMainProductGroupId(mainVariantId, groupId);
      }

      await this.addAddonToCart(
        variantId,
        mainVariantId,
        cartQuantity,
        groupId,
        groupIndex,
        null,
        null
      );
      await this.refreshCartUI();

      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      }

      this.hideGlobalLoading();
    } catch (error) {
      console.error('Error adding simple addon:', error);
      input.checked = false;
      this.hideGlobalLoading();
      alert(error instanceof Error ? error.message : 'Unable to add item to cart');
    }
  }

  /**
   * @param {string} variantId
   * @param {string} mainGroupId
   * @param {HTMLInputElement | null} input
   * @param {boolean} skipRefresh
   */
  async handleSimpleAddonRemove(variantId, mainGroupId, input, skipRefresh) {
    try {
      const cartResponse = await fetch('/cart.json');
      const cart = await cartResponse.json();
      const items = /** @type {Array<Record<string, unknown>>} */ (cart.items);

      let itemLine = -1;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item) continue;
        const props = /** @type {Record<string, string>} */ (item['properties'] ?? {});
        if (item['variant_id'] == variantId && props['_product_group_id'] === mainGroupId) {
          itemLine = i + 1;
          break;
        }
      }

      if (itemLine !== -1) {
        await this.removeAddonLine(itemLine);
        if (!skipRefresh) await this.refreshCartUI();
      }

      if (!skipRefresh) {
        setTimeout(() => this.hideGlobalLoading(), 500);
      }
    } catch (error) {
      console.error('Error removing simple addon:', error);
      if (input) input.checked = true;
      if (!skipRefresh) this.hideGlobalLoading();
      throw error;
    }
  }

  /** @param {HTMLElement} button */
  handleSimpleRemoveButtonClick(button) {
    const option = button.closest('.cart-simple-addon-option-new');
    const input = /** @type {HTMLInputElement | null} */ (
      option?.querySelector('.cart-simple-addon-checkbox-new, .cart-simple-addon-radio-new')
    );

    if (!input) return;

    const variantId = input.dataset.variantId ?? '';
    const group = /** @type {HTMLElement | null} */ (input.closest('.cart-simple-addon-group-new'));
    const mainGroupId = group?.dataset.mainGroupId ?? '';

    this.showGlobalLoading('Removing item...');

    this.handleSimpleAddonRemove(variantId, mainGroupId, input, false)
      .then(() => {
        input.checked = false;
      })
      .catch((error) => {
        console.error('Error removing addon:', error);
        this.hideGlobalLoading();
      });
  }

  // ============================================================================
  // REFRESH CART UI
  // ============================================================================

  async refreshCartUI() {
    const cartComponents = /** @type {NodeListOf<HTMLElement>} */ (
      document.querySelectorAll('cart-items-component')
    );
    if (!cartComponents.length) {
      console.error('cart-items-component not found');
      return;
    }

    // Collect unique section IDs across all cart-items-component elements
    const sectionIds = new Set(
      /** @type {string[]} */ (
        Array.from(cartComponents)
          .map((el) => el.dataset.sectionId)
          .filter(Boolean)
      )
    );

    try {
      // Fetch real item count and section HTML in parallel
      const [cartData] = await Promise.all([
        fetch('/cart.js').then((r) => r.json()),
        ...Array.from(sectionIds).map(async (sectionId) => {
          // Always fetch from /cart — works regardless of current page
          const response = await fetch(`/cart?section_id=${sectionId}`);
          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newContent = html.querySelector('cart-items-component');

          if (newContent) {
            const target = document.querySelector(
              `cart-items-component[data-section-id="${sectionId}"]`
            );
            if (target) target.innerHTML = newContent.innerHTML;
          }
        }),
      ]);

      const itemCount = /** @type {number} */ (cartData?.item_count ?? 0);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { itemCount, source: 'cart-add-on-new' } },
        })
      );

      // Open the cart drawer if it exists and we're not already on the cart page.
      const isCartPage = window.location.pathname === '/cart';
      if (!isCartPage && !document.getElementById('atc-popup-dialog')) {
        const cartDrawer = /** @type {Element & { open?: () => void } | null} */ (
          document.querySelector('cart-drawer-component')
        );
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }
    } catch (error) {
      console.error('Error refreshing cart UI:', error);
    }
  }

  // ============================================================================
  // AUTO-REMOVE ORPHANED ADD-ON ITEMS
  // ============================================================================

  async removeOrphanedAddons() {
    try {
      const response = await fetch('/cart.json');
      const cart = await response.json();
      const items = /** @type {Array<Record<string, unknown>>} */ (cart.items);

      // Build a set of composite keys ("variantId:groupId") for all main (non-addon) items.
      // Using the composite key ensures that when two identical products are in the cart
      // (one with a group ID, one without), removing one doesn't orphan the other's upsells.
      const currentMainKeys = new Set();
      for (let j = 0; j < items.length; j++) {
        const cartItem = items[j];
        if (!cartItem) continue;
        const props = /** @type {Record<string, string>} */ (cartItem['properties'] ?? {});
        if (!props['_upsell_for']) {
          const groupId = props['_product_group_id'] ?? '';
          currentMainKeys.add(String(cartItem['variant_id']) + ':' + groupId);
        }
      }

      // Collect 1-based line numbers of addon items whose specific parent is gone.
      const orphanedLines = /** @type {number[]} */ ([]);
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item) continue;
        const props = /** @type {Record<string, string>} */ (item['properties'] ?? {});
        const addonFor = props['_upsell_for'];
        if (addonFor) {
          const addonGroupId = props['_product_group_id'] ?? '';
          const compositeKey = String(addonFor) + ':' + addonGroupId;
          if (!currentMainKeys.has(compositeKey)) {
            orphanedLines.push(i + 1);
          }
        }
      }

      if (orphanedLines.length === 0) {
        // No orphans, but still refresh so the cart:update 'cart-add-on-new'
        // event fires and loading overlays can hide.
        await this.refreshCartUI();
        return;
      }

      // Build an updates object keyed by 1-based line number, setting each to 0.
      // Shopify's /cart/update.js accepts { updates: { "1": qty, "2": qty, ... } }
      // when keys are line numbers, allowing us to remove all orphans in ONE request.
      const updates = /** @type {Record<string, number>} */ ({});
      for (const line of orphanedLines) {
        updates[String(line)] = 0;
      }
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) {
        // Fallback: remove sequentially if batch update fails
        orphanedLines.reverse();
        for (const line of orphanedLines) {
          await this.removeAddonLine(line);
        }
      }

      await this.refreshCartUI();

      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      }
    } catch (error) {
      console.error('Error removing orphaned add-ons:', error);
    }
  }

  // ============================================================================
  // EVENT LISTENERS (using event delegation)
  // ============================================================================

  setupDrawerTriggers() {
    document.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target);
      const trigger = /** @type {HTMLElement | null} */ (target.closest('.cart-addon-trigger-new'));
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleCartAddonDropdown(trigger);
      }
    });
  }

  setupSimpleCheckboxes() {
    document.addEventListener('change', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target);
      if (target.classList.contains('cart-simple-addon-checkbox-new')) {
        this.handleSimpleAddonChange(target);
      }
    });
  }

  setupSimpleRemoveButtons() {
    document.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target);
      const removeBtn = /** @type {HTMLElement | null} */ (
        target.closest('.cart-simple-remove-btn-new')
      );
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleSimpleRemoveButtonClick(removeBtn);
      }
    });
  }

  setupGroupMoreInfoPopups() {
    // Open popup
    document.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target);
      const trigger = target.closest('.group-more-info-trigger-new');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        const groupContainer =
          trigger.closest('.cart-addon-group-new') ||
          trigger.closest('.cart-simple-addon-group-new');
        const popup = groupContainer?.querySelector('.group-more-info-new.pop-up');
        if (popup) {
          popup.classList.add('active');
          document.body.style.overflow = 'hidden';
        }
      }
    });

    // Close popup (X button or backdrop click)
    document.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target);

      const closeBtn = target.closest('.group-more-info-close-new');
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const popup = closeBtn.closest('.group-more-info-new.pop-up');
        if (popup) {
          popup.classList.remove('active');
          document.body.style.overflow = '';
        }
        return;
      }

      if (target.classList.contains('group-more-info-new') && target.classList.contains('pop-up')) {
        /** @type {HTMLElement} */ (target).style.display = '';
        target.classList.remove('active');
        document.body.style.overflow = '';
      }
    });
  }

  setupAddonCleanup() {
    // After a cart:update event from the native cart component, clean up orphaned add-ons
    document.addEventListener('cart:update', async (event) => {
      const customEvent = /** @type {CustomEvent<{ data?: { source?: string } }>} */ (event);
      if (customEvent.detail?.data?.source === 'cart-items-component') {
        await this.removeOrphanedAddons();
      }
    });
  }
}

// ============================================================================
// GLOBAL ONCLICK HANDLERS (required by Liquid inline onclick attributes)
// ============================================================================

/**
 * Handles adding a drawer-mode addon to the cart.
 * Called via onclick='handleCartAddonAdd(this)' in cart-add-on-new.liquid.
 * @param {HTMLButtonElement} button
 */
window.handleCartAddonAdd = async function (button) {
  const manager = window._cartAddonManager;
  if (!manager) return;

  // If this row is already selected, treat as a remove
  if (button.classList.contains('simple-dropdown-row-new') && button.classList.contains('active')) {
    const removeLine = button.dataset.removeLine ?? '';
    if (removeLine) {
      manager.showGlobalLoading('Removing item...');
      try {
        await manager.removeAddonLine(parseInt(removeLine));
        await manager.refreshCartUI();
        if (typeof window.refreshCart === 'function') window.refreshCart();
      } catch (error) {
        console.error('Error removing addon:', error);
      } finally {
        manager.hideGlobalLoading();
      }
    }
    return;
  }

  const variantId = button.dataset.variantId ?? '';
  const mainVariantId = button.dataset.mainVariantId ?? '';
  const mainGroupId = button.dataset.mainGroupId ?? '';
  const cartQuantity = parseInt(button.dataset.cartQuantity ?? '1') || 1;
  const addonGroupIndex = button.dataset.addonGroupIndex ?? '';
  const categoryIndex = button.dataset.categoryIndex || null;
  const categoryTitle = button.dataset.categoryTitle || null;

  manager.showGlobalLoading('Adding item to cart...');
  button.disabled = true;

  try {
    let groupId = mainGroupId;
    if (!groupId || groupId === '') {
      groupId = manager.generateUniqueId();
      groupId = await manager.updateMainProductGroupId(mainVariantId, groupId);
    }

    // Remove any existing addon for this group + category before adding the new one
    const cartResponse = await fetch('/cart.json');
    const cart = await cartResponse.json();
    const items = cart.items;
    let existingAddonLine = -1;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      const props = item['properties'] ?? {};
      if (
        props['_is_upsell'] === 'true' &&
        props['_upsell_for'] == mainVariantId &&
        props['_addon_group_index'] == addonGroupIndex &&
        props['_product_group_id'] === groupId &&
        (categoryIndex === null || props['_addon_category_index'] == categoryIndex)
      ) {
        existingAddonLine = i + 1;
        break;
      }
    }
    if (existingAddonLine !== -1) {
      await manager.removeAddonLine(existingAddonLine);
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    await manager.addAddonToCart(
      variantId,
      mainVariantId,
      cartQuantity,
      groupId,
      addonGroupIndex,
      categoryIndex,
      categoryTitle
    );

    await manager.refreshCartUI();

    if (typeof window.refreshCart === 'function') {
      window.refreshCart();
    }

    manager.hideGlobalLoading();
  } catch (error) {
    console.error('Error adding addon:', error);
    manager.hideGlobalLoading();
    alert(error instanceof Error ? error.message : 'Unable to add item to cart');
    button.disabled = false;
  }
};

/**
 * Handles removing a drawer-mode addon from the cart.
 * Called via onclick='handleCartAddonRemove(this)' in cart-add-on-new.liquid.
 * @param {HTMLButtonElement} button
 */
window.handleCartAddonRemove = function (button) {
  const manager = window._cartAddonManager;
  if (!manager) return;

  const line = parseInt(button.dataset.line ?? '0');
  const cartComponent =
    /** @type {HTMLElement & { updateQuantity?: (opts: { line: number, quantity: number, action: string }) => void } | null} */ (
      document.querySelector('cart-items-component')
    );

  manager.showGlobalLoading('Removing item...');
  button.disabled = true;

  if (cartComponent && typeof cartComponent.updateQuantity === 'function') {
    cartComponent.updateQuantity({ line, quantity: 0, action: 'remove' });

    setTimeout(() => {
      manager.hideGlobalLoading();
    }, 800);
  } else {
    manager.hideGlobalLoading();
    button.disabled = false;
  }
};

// ============================================================================
// INITIALIZE
// ============================================================================

document.addEventListener('DOMContentLoaded', function () {
  const manager = new CartAddonManager();
  window._cartAddonManager = manager;
  manager.init();
});
